import { useState } from 'react';
import { IExercise } from '../models/exercise';
import { useContentFavorite } from '../../modules/Disciplines/hooks/useContentFavorite';
import { IVideo } from '../models/video';
import { IFeedbackSimulationExercise } from '../../modules/GeneralSimulation/models';

interface IResolutionData {
  resolutionVideo: IVideo;
  exerciseId: string;
}

export const useExamExerciseListHook = () => {
  const [resolutionData, setResolutionData] = useState<IResolutionData>(
    {} as IResolutionData
  );
  const [isResolutionModalOpen, setResolutionModalOpen] =
    useState<boolean>(false);

  const { mutate } = useContentFavorite();

  const exerciseTypes = {
    single_answer: 'única',
    multiple_answer: 'de múltipla escolha',
    discursive: 'discursiva',
  };

  const favoriteAction = (exercise: IExercise) => {
    mutate({
      category: 'exercise',
      targetId: exercise.id,
      targetType: 'Exercise',
    });

    exercise.favorite = !exercise.favorite;

    return exercise;
  };

  const convertMathGraphs = () => {
    if (typeof window?.MathJax !== 'undefined') {
      window.MathJax.typesetPromise();
    }
  };

  const setExerciseAlternative = (
    alternativeId: string,
    exercise: IExercise
  ) => {
    if (!exercise.done) {
      exercise.hasAlternativeSelected = true;
      exercise.alternatives = exercise.alternatives.map((alternative) => {
        if (alternative.id === alternativeId) {
          const isMultiple =
            exercise.exerciseType === 'multiple_answer' ||
            exercise.exercise_type === 'multiple_answer';

          alternative.selected = isMultiple ? !alternative.selected : true;
        } else {
          // Para escolha única, desmarcar outras
          if (
            !exercise.exerciseType ||
            exercise.exerciseType === 'single_answer' ||
            !exercise.exercise_type ||
            exercise.exercise_type === 'single_answer'
          ) {
            alternative.selected = false;
          }
        }
        return alternative;
      });
    }

    return exercise;
  };

  const setMultipleExerciseAlternatives = (
    alternativeIds: string[],
    exercise: IExercise
  ) => {
    if (!exercise.done) {
      exercise.hasAlternativeSelected = alternativeIds.length > 0;
      exercise.alternatives = exercise.alternatives.map((alternative) => {
        alternative.selected = alternativeIds.includes(
          alternative.id || alternative._id
        );
        return alternative;
      });
    }

    return exercise;
  };

  const tryAgain = (exercise: IExercise) => {
    exercise.done = false;
    exercise.showAnswer = false;
    exercise.hasAlternativeSelected = false;
    exercise.alternatives = exercise!.alternatives.map((alternative) => {
      ((alternative.selected = false), (alternative.status = 'default'));

      return alternative;
    });

    return exercise;
  };

  const viewAnswer = (exercise: IExercise) => {
    exercise.showAnswer = !exercise.showAnswer;
    exercise.alternatives = exercise!.alternatives.map((alternative) => {
      if (alternative.correct) {
        alternative.status = exercise.showAnswer ? 'correct' : 'default';
      }

      return alternative;
    });

    return exercise;
  };

  const showResolution = (exercise: IExercise) => {
    if (exercise.resolution_video) {
      setResolutionData({
        resolutionVideo: exercise.resolution_video,
        exerciseId: exercise.id,
      });
      setResolutionModalOpen(true);
    }
  };

  const setGeneralSimulationResolution = (
    exercise: IFeedbackSimulationExercise
  ) => {
    if (exercise.resolution_video) {
      setResolutionData({
        resolutionVideo: exercise.resolution_video as IVideo,
        exerciseId: exercise.id,
      });
      setResolutionModalOpen(true);
    }
  };

  return {
    exerciseTypes,
    resolutionData,
    isResolutionModalOpen,
    favoriteAction,
    setExerciseAlternative,
    setMultipleExerciseAlternatives,
    tryAgain,
    viewAnswer,
    convertMathGraphs,
    showResolution,
    setResolutionModalOpen,
    setGeneralSimulationResolution,
  };
};
